import { useState, useRef, useEffect, useMemo } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { COUNTRIES } from "@/data/questions";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getUiTranslations } from "@/lib/ui-translations";
import { getLocalizedOptionLabel } from "@/lib/option-labels";

const PINNED_CODES = new Set(["CA", "FR"]);
const pinnedCountries = COUNTRIES.filter((c) => PINNED_CODES.has(c.code));
const otherCountries = COUNTRIES.filter((c) => !PINNED_CODES.has(c.code));

// Codes ISO sans drapeau emoji défini (Unicode n'en attribue pas).
const NO_FLAG = new Set(["XK"]);

/**
 * Drapeau emoji dérivé du code ISO à deux lettres. Purement décoratif :
 * la valeur enregistrée reste le NOM du pays, jamais le drapeau, pour que
 * le remplissage des formulaires et la révision en console soient intacts.
 */
function countryFlag(code: string): string {
  if (code.length !== 2 || NO_FLAG.has(code)) return "";
  return String.fromCodePoint(
    ...[...code.toUpperCase()].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65),
  );
}

function CountryLabel({ country, language }: { country: { code: string; name: string }; language: string }) {
  const flag = countryFlag(country.code);
  return (
    <span className="flex items-center gap-2">
      {flag && (
        <span aria-hidden="true" className="text-base leading-none">
          {flag}
        </span>
      )}
      <span>{getLocalizedOptionLabel(country, language)}</span>
    </span>
  );
}

interface CountryComboboxProps {
  /** Accepts either a country code ("CA") or name ("Canada") for backwards compat */
  value: string;
  /** Now returns the country **name** (e.g. "Canada"), not the code */
  onValueChange: (name: string) => void;
  placeholder?: string;
  className?: string;
  searchPlaceholder?: string;
  language?: string;
}

export function CountryCombobox({
  value,
  onValueChange,
  placeholder,
  className,
  searchPlaceholder,
  language = "fr",
}: CountryComboboxProps) {
  const t = getUiTranslations(language);
  const effectivePlaceholder = placeholder ?? t.selectCountryPlaceholder;
  const effectiveSearchPlaceholder = searchPlaceholder ?? t.searchCountry;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  // Support both old code format ("CA") and new name format ("Canada")
  const selectedCountry = useMemo(
    () => COUNTRIES.find((c) => c.code === value) || COUNTRIES.find((c) => c.name === value),
    [value],
  );
  const selectedName = useMemo(
    () => (selectedCountry ? getLocalizedOptionLabel(selectedCountry, language) : undefined),
    [selectedCountry, language],
  );

  const normalize = (s: string) =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const q = search ? normalize(search) : "";
  const filtered = search
    ? COUNTRIES.filter((c) => {
        const localizedName = getLocalizedOptionLabel(c, language);
        return normalize(c.name).includes(q) || normalize(localizedName).includes(q);
      })
    : [...pinnedCountries, ...otherCountries];

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightIndex(-1);
  }, [search]);

  // Focus the search input when popover opens
  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll("[data-country-item]");
    items[highlightIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex]);

  const select = (country: (typeof COUNTRIES)[number]) => {
    onValueChange(country.name);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      select(filtered[highlightIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showPinnedSection = !search && pinnedCountries.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-12 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
        >
          <span className={cn("flex items-center gap-2", !selectedName && "text-muted-foreground")}>
            {selectedCountry && countryFlag(selectedCountry.code) && (
              <span aria-hidden="true" className="text-base leading-none">
                {countryFlag(selectedCountry.code)}
              </span>
            )}
            {selectedName || effectivePlaceholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div onKeyDown={handleKeyDown}>
          {/* Search input */}
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={effectiveSearchPlaceholder}
              className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          {/* Country list */}
          <div ref={listRef} className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t.noCountryFound}
              </p>
            )}

            {showPinnedSection && (
              <>
                <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {t.pinnedCountries}
                </p>
                {pinnedCountries.map((c, i) => (
                  <button
                    key={c.code}
                    type="button"
                    data-country-item
                    onClick={() => select(c)}
                    className={cn(
                      "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
                      highlightIndex === i
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedCountry?.code === c.code ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <CountryLabel country={c} language={language} />
                  </button>
                ))}
                <div className="-mx-1 my-1 h-px bg-border" />
              </>
            )}

            {(search ? filtered : otherCountries).map((c, i) => {
              const idx = showPinnedSection
                ? i + pinnedCountries.length
                : i;
              return (
                <button
                  key={c.code}
                  type="button"
                  data-country-item
                  onClick={() => select(c)}
                  className={cn(
                    "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
                    highlightIndex === idx
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedCountry?.code === c.code ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <CountryLabel country={c} language={language} />
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
